import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate,useLocation } from 'react-router-dom';
import Dropdown from '../dropdown/dropdown';
import Button from '../button/button';
import { notify } from '../notifier/notifier';
import { adminPanelOption, userOptions } from './data';
import Menu from '../menu/menu';
import { List, MapPin, X } from '@phosphor-icons/react';
import LoginModal from '../../routes/login/loginModal';
import './style';
import useLoginModal from '../../routes/login/useLoginModal';
import { getEventsList } from '../../routes/landing-page/api';
import { analyticEventLogger } from '../../utils/commonUtils';
import { SELLER_TICKET_NAVBAR_EVENT_CLICK } from '../../constants/eventLogger';
import mixpanel from 'mixpanel-browser';
import { generateGuestToken } from '../../routes/login/api';

const Navbar = () => {
  const { selectedCity } = useSelector((state) => state.landingPage);

  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  const { userData, cities } = useSelector((state) => state.app);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const loginModal = useLoginModal();
  const imageUrl = userData?.picture;

  const profileOptions = useMemo(() => {
    const options = userOptions.buyer || [];
    return userData?.isAdmin ? [...adminPanelOption, ...options] : [...options];
  }, [userData]);

  useEffect(() => {
    if (selectedCity) {
      getEventsList(selectedCity.label);
    }
  }, [selectedCity]);

  const handleOverlayToggle = () => {
    setIsOverlayOpen(!isOverlayOpen);
  };

  const onCreateEventClick = () => {
    if (location.pathname == '/landing')
    {
        const url = '/event/create';
        if (!userData?.email) {
          loginModal.onOpen();
          loginModal.callback.current = () => navigate(url);
        } else {
          navigate(url);
        }
    }
    else{
      handleOverlayToggle();
      navigate('/landing');
    }
  };

  const onProfileOptionClick = (menuId) => {
    switch (menuId) {
    case 'user-profile':
    case 'my-listing':
    case 'events':
    case 'bookings':
    case 'admin/primary':
    case 'admin/events':
      navigate(`/${menuId}`);
      break;
    case 'support':
      window.open('mailto:rescue@takemytickets.co.in');
      break;
    case 'logout': {
      navigate('/');
      notify('success', 'logged out');
      dispatch({ type: 'APP:REMOVE_JWT_TOKEN' });
      generateGuestToken();
      break;
    }
    default:
      navigate('/');
    }
  };

  const onResellClick = () => {
    analyticEventLogger(SELLER_TICKET_NAVBAR_EVENT_CLICK, {
      isAuthenticated: !!userData?.email,
    });
    if (!userData?.email) {
      loginModal.onOpen();
      loginModal.callback.current = () => navigate('/sell-tickets');
    } else {
      mixpanel.track("Navbar_Resell_Tickets_Clicked", {
        "event_id": null,   
        "event_name": "N/A",
        "user_id": userData?._id || "Anonymous",
        "page_url": window.location.href,
      });

      navigate('/sell-tickets');
    }
  };

  const handleNavigate = () => {
    if (location.pathname !== '/all-events') {
      navigate('/');
    }
  };

  return (
    <>
      <header className="navbar-container flex flex-row w-full md:h-[100px] h-[72px] py-[18px] px-6 sm:px-6 md:px-[130px] lg:px-[130px] xl:px-[200px] justify-between items-center flex-shrink-0 bg-white fixed z-20 mx-auto">
        <div className="flex items-center gap-[12px] md:gap-[20px] justify-center flex-shrink">
          <img
            className="cursor-pointer h-8 md:h-10"
            src="assets/images/logo-primary.svg"
            alt="logo"
            onClick={() => navigate('/')}
          />
          <Dropdown
            className="city-selector h-auto"
            options={cities || []}
            placeholder={selectedCity?.label}
            value={selectedCity?.id}
            iconLeft={<MapPin size={24}
              color="#101010"
              weight="fill" />}
            hasSearch={cities?.length > 10}
            onChange={(value) => {
              const selectedOption = cities?.find(
                (option) => option.id === value,
              );
              dispatch({
                type: 'SELECTED_CITY:DATA:SET',
                data: selectedOption,
              });
              localStorage.setItem(
                'selectedCity',
                JSON.stringify(selectedOption),
              );
              handleNavigate();
            }}
            width="170px"
          />
        </div>
        <div className="flex items-center gap-[12px] justify-center flex-shrink">
          <Button
            type="tertiary-text"
            onClick={onResellClick}
            className="navbar-button hidden lg:flex"
            size="small"
          >
            Re-sell tickets
          </Button>
          <Button
            type="primary"
            onClick={onCreateEventClick}
            className="navbar-button hidden lg:flex w-[185px]"
            size="small"
          >
            List your event
          </Button>
          {userData?.email ? (
            <div className="relative nav-menu hidden lg:flex items-center gap-2 px-2 py-2 rounded-[12px] shadow border border-[#B7B7B7] bg-[#FDFDFD]">
              <Menu
                className="mt-2"
                triggerIcon={
                  <div className="flex items-center gap-2">
                    <img
                      className="cursor-pointer rounded-full w-7 h-7 object-cover"
                      src={imageUrl}
                      alt={userData?.name}
                    />
                    <List size="16"
                      color="#101010" />
                  </div>
                }
                menuItems={profileOptions.map((option) => ({
                  label: (
                    <div className="flex items-center gap-2">
                      {option.icon}
                      {option.label}
                    </div>
                  ),
                  id: option.id,
                }))}
                onClick={(item) => onProfileOptionClick(item.id)}
              />
            </div>
          ) : (
            <Button
              type="tertiary"
              onClick={() => loginModal?.onOpen()}
              className="navbar-button hidden lg:flex"
            >
              Login/Signup
            </Button>
          )}
        </div>
        <div className="flex items-center lg:hidden justify-center flex-shrink">
          {isOverlayOpen ? (
            <X
              size="32"
              weight="light"
              color="#101010"
              onClick={() => handleOverlayToggle()}
            />
          ) : (
            <List
              size="32"
              color="#101010"
              onClick={() => handleOverlayToggle()}
            />
          )}
        </div>
      </header>

      {/* Mobile Overlay */}
      {isOverlayOpen && (
        <div className="mobile-overlay fixed z-50 lg:hidden top-[72px] left-0 right-0 bottom-0 flex flex-col w-full bg-white overflow-auto">
          {userData?.email ? (
            <>
              <div className="flex flex-row py-4 px-6 space-x-3 items-center cursor-pointer w-full">
                <img
                  className="cursor-pointer rounded-full w-7 h-7 object-cover"
                  src={imageUrl}
                  alt={userData?.name}
                />
                <h5 className="font-sans text-base font-semibold leading-5">
                  {userData?.name || userData?.email}
                </h5>
              </div>
              <div className="border-t border-[#D9D9D9]" />
              {profileOptions.map((option) => (
                <div
                  key={option.id}
                  className="flex flex-row space-x-3 py-4 px-6 items-center cursor-pointer"
                  onClick={() => {
                    onProfileOptionClick(option.id);
                    handleOverlayToggle();
                  }}
                >
                  {option.icon}
                  <h5 className="font-sans text-base font-medium">
                    {option.label}
                  </h5>
                </div>
              ))}
              <div className="flex flex-1 flex-col space-y-4 px-6 mt-[40px] w-full">
                <Button
                  type="primary"
                  onClick={() => onCreateEventClick()}
                  className="w-full h-[44px] justify-center items-center"
                  size="small"
                >
                  List your event
                </Button>
                <Button
                  type="tertiary"
                  onClick={() => {
                    onResellClick();
                    handleOverlayToggle();
                  }}
                  className="w-full h-[44px] justify-center items-center"
                >
                  Re-sell tickets
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col space-y-4 px-6 mt-10 w-full">
                <Button
                  type="primary"
                  onClick={() => {
                    onCreateEventClick();
                    handleOverlayToggle();
                  }}
                  className="w-full h-[44px] justify-center items-center"
                  size="small"
                >
                  List your event
                </Button>
                <Button
                  type="tertiary"
                  onClick={() => {
                    loginModal?.onOpen();
                    handleOverlayToggle();
                  }}
                  className="w-full h-[44px] justify-center items-center"
                >
                  Login/Signup
                </Button>
              </div>
              <div className="flex px-6 mt-3 items-center justify-center">
                <button
                  type="tertiary"
                  className="bg-gray-200 navbar-button px-5 rounded-lg"
                  onClick={() => {
                    onResellClick();
                    handleOverlayToggle();
                  }}
                >
                  Re-sell tickets
                </button>
              </div>
            </>
          )}
        </div>
      )}
      <LoginModal />
    </>
  );
};

export default Navbar;
